/**
 * @author Simeon Artamonov <simeon@cross-works.co.uk>
 * @company Cross Works Ltd
 */
import { ref, watch, onMounted, onBeforeUnmount } from 'vue'
import { useRouter, useRoute } from 'vue-router'

import { useStrapiStore } from '../stores/strapi'
import { useAppStateStore } from '../stores/appState'
import useTheme from './useTheme'
import { useMousePreview } from './useMousePreview'

// Panel Configuration Constants
export const PANEL_CONFIG = {
  // Cache and performance
  CACHE_DURATION: 100, // milliseconds
  MOBILE_BREAKPOINT: 991,
  
  // Click/drag detection  
  DRAG_THRESHOLD_BASE: 10, // pixels (scaled by devicePixelRatio)
  CLICK_TIMEOUT: 300, // milliseconds
  
  // Scroll behavior
  SCROLL_FADE_DELAY: 300, // milliseconds
  
  // Transition timings
  TRANSITION_FALLBACK: 300, // milliseconds
  FADE_DURATION: 200, // milliseconds
  POSITION_DURATION: 300, // milliseconds
  
  // Panel positioning
  PREVIEW_OFFSET_DESKTOP: 80, // percentage
  PREVIEW_OFFSET_HOVER: 78, // percentage
  
  // Observer margins
  INTERSECTION_ROOT_MARGIN: '100px',
  
  // Initialization delays
  OBSERVER_INIT_DELAY: 100, // milliseconds
  RESIZE_DEBOUNCE: 150, // milliseconds
  
  // Transform positions (for lookup table)
  COMMON_POSITIONS: [
    -95, -92, -90, -85, -80, -78, -75, -70, -65, -60,  // Left panel range
    60, 65, 70, 75, 78, 80, 85, 90, 92, 95,           // Right panel range
    -100, 0, 100                                       // Edge cases
  ]
} as const

export const PANEL_EASING = {
  FADE: 'ease-out',
  POSITION: 'cubic-bezier(0.16, 1, 0.3, 1)',
  PREVIEW: 'cubic-bezier(0.16, 1, 0.3, 1)'
} as const

type Side = 'left' | 'right' | null

export function usePanels() {
  const router = useRouter()
  const route = useRoute()

  const strapiStore = useStrapiStore()
  const appStateStore = useAppStateStore()
  const { setPanelTheme, restorePreviousTheme } = useTheme()
  const { initializeListeners: reinitializeMousePreview, isMobile } = useMousePreview()

  const active = ref<Side>(null)
  const previous = ref<string>(route.fullPath)

  // Mobile menu state (consolidated from useNavigation)
  const menuOpen = ref(false)

  
  // Track scroll positions for each panel
  const panelScrollPositions = ref<{
    left: number;
    right: number;
  }>({
    left: 0, 
    right: 0
  })

  // Store main page scroll position
  const mainPageScroll = ref(0)

  if ((route.path === '/design' || route.path === '/technology') && appStateStore.currentState === 'initializing') {
    const side = route.path === '/design' ? 'left' : 'right'
    appStateStore.requestPanelEntrance(side)
    appStateStore.setShouldDelayEntrance(true)
    appStateStore.setGracePeriod(350)
  }

  // Watch for appState requesting panel entrance
  watch(() => appStateStore.activePanelSide, (newSide, oldSide) => {
    if (newSide && newSide !== oldSide) {
      // AppState wants to open a panel
      openViaAppState(newSide)
    } else if (!newSide && oldSide) {

      closePanel(true)
    }
  })

  // Automatically load panel data when panels are requested
  watch(() => appStateStore.pendingPanelEntrance, (pendingSide) => {
    if (pendingSide) {
      loadPanelData(pendingSide)
    }
  })

  // Load panel data on demand
  async function loadPanelData(side: 'left' | 'right') {
    if (side === 'left') {
      await strapiStore.initializeDesign()
      appStateStore.setPanelDataLoaded('left', true)
    } else if (side === 'right') {
      await strapiStore.initializeTechnology()
      appStateStore.setPanelDataLoaded('right', true)
    }
  }

  // keep track of last non-panel path
  watch(route, (to) => {
    if (!active.value) previous.value = to.fullPath
  })

  // Save current scroll position before opening panel
  function saveMainPageScroll() {
    mainPageScroll.value = window.scrollY
  }

  // Restore main page scroll position
  function restoreMainPageScroll() {
    window.scrollTo(0, mainPageScroll.value)
  }

  // Save panel scroll position
  function savePanelScroll(side: Side, position: number) {
    if (side) {
      panelScrollPositions.value[side] = position
    }
  }

  // Get panel scroll position
  function getPanelScroll(side: Side): number {
    return side ? panelScrollPositions.value[side] : 0
  }



  function openViaAppState(side: 'left' | 'right') {
    openPanel(side)
  }


  async function open(side: 'left' | 'right') {
    await loadPanelData(side)
    openPanel(side)
  }
  
  // Core panel opening mechanics
  function openPanel(side: 'left' | 'right') {
    saveMainPageScroll()
    active.value = side
    const slug = side === 'left' ? 'design' : 'technology'
    
    document.documentElement.classList.add('panel-open')
    document.body.classList.add('panel-open')
    
    document.body.classList.add('panel-opening')
    setTimeout(() =>{
      document.body.classList.remove('panel-opening')
    }, 1000)


    
    // Apply panel theme
    if (side === 'left' && strapiStore.designContent?.pageStyle) {
      setPanelTheme(strapiStore.designContent.pageStyle, 'xw-yellow')
    } else if (side === 'right' && strapiStore.technologyContent?.pageStyle) {
      setPanelTheme(strapiStore.technologyContent.pageStyle, 'xw-yellow')
    }
    
    // Update appState to keep it in sync
    if (appStateStore.currentState !== 'panel-open') {
      appStateStore.activePanelSide = side
      appStateStore.transitionTo('panel-open', `manual ${side} panel opened`)
    }
    
    // Only update URL if not already on the correct route
    if (route.path !== `/${slug}`) {
      router.replace({ path: `/${slug}` })
    }
  }

  // close panel and restore URL
  function close(skipNavigation = false) {
    closePanel(skipNavigation)
  }
  
  // Core panel closing mechanics
  function closePanel(skipNavigation = false) {
    active.value = null
    restoreMainPageScroll()
    
    // Remove panel-open class from html and body to restore document scrolling
    document.documentElement.classList.remove('panel-open')
    document.body.classList.remove('panel-open')
    
    // Restore previous theme
    restorePreviousTheme()
    
    // Reinitialize mouse preview listeners in case they were cleaned up
    try {
      reinitializeMousePreview()
    } catch (error) {
      console.debug('Mouse preview reinitialization skipped:', error)
    }
    
    // Update appState to keep it in sync
    if (appStateStore.currentState === 'panel-open') {
      appStateStore.exitPanel()
    }
    

    if (!skipNavigation && (route.path === '/design' || route.path === '/technology')) {
      const targetRoute = (previous.value === '/design' || previous.value === '/technology') 
        ? '/' 
        : previous.value
        
      router.replace({ path: targetRoute })
    }
  }

  // when user navigates (back/forward), update panel state
  watch(() => route.path, async (p) => {
    if (p === '/design') {
      if (active.value !== 'left') {
        if (appStateStore.currentState === 'initializing' || appStateStore.currentState === 'loading-for-panel') {
          appStateStore.requestPanelEntrance('left')
        } else {
          await open('left')
        }
      }
    } else if (p === '/technology') {
      if (active.value !== 'right') {
        if (appStateStore.currentState === 'initializing' || appStateStore.currentState === 'loading-for-panel') {
          appStateStore.requestPanelEntrance('right')
        } else {
          await open('right')
        }
      }
    } else {
      if (active.value !== null) {
        // User navigated to non-panel route, close panel but don't override navigation
        close(true) // skipNavigation = true
      }
    }
  })

  // Mobile menu functions (consolidated from useNavigation)
  const toggleMenu = () => {
    if (!active.value) menuOpen.value = !menuOpen.value
  }

  const closeMenu = () => {
    menuOpen.value = false
  }

  const handleMobilePanel = (side: 'left' | 'right') => {
    if (menuOpen.value) closeMenu()
    active.value === side ? close() : open(side)
  }

  // Mobile resize handler
  const handleResize = () => {
    if (isMobile.value && active.value) close()
  }

  // Keyboard controls for panels and navigation
  function handleKeydown(event: KeyboardEvent) {
    // Only handle if no input/textarea is focused and no modifiers
    if (
      event.target instanceof HTMLInputElement || 
      event.target instanceof HTMLTextAreaElement ||
      event.ctrlKey || 
      event.metaKey || 
      event.altKey
    ) {
      return
    }

    switch (event.key) {
      // case 'ArrowRight':
      //   event.preventDefault()
      //   if (active.value !== 'left') {
      //     // Always open/switch to left panel (design)
      //     open('left')
      //   } else {
      //     // Left panel already open → close it
      //     close()
      //   }
      //   break
        
      // case 'ArrowLeft':
      //   event.preventDefault()
      //   if (active.value !== 'right') {
      //     // Always open/switch to right panel (technology)
      //     open('right')
      //   } else {
      //     // Right panel already open → close it
      //     close()
      //   }
      //   break
        
      case 'Escape':
        event.preventDefault()
        if (menuOpen.value) {
          // Close mobile menu first
          closeMenu()
        } else if (active.value !== null) {
          // Close any open panel
          close()
        }
        break
    }
  }

  // Add keyboard and resize event listeners
  onMounted(() => {
    window.addEventListener('keydown', handleKeydown)
    window.addEventListener('resize', handleResize, { passive: true })
  })

  onBeforeUnmount(() => {
    window.removeEventListener('keydown', handleKeydown)
    window.removeEventListener('resize', handleResize)
    // Clean up html and body classes on unmount
    document.documentElement.classList.remove('panel-open')
    document.body.classList.remove('panel-open')
    // Restore theme on unmount
    restorePreviousTheme()
    reinitializeMousePreview()
  })

  return { 
    // Panel state
    active, 
    previous,
    
    // Panel actions
    open, 
    close,
    savePanelScroll,
    getPanelScroll,
    
    // Mobile navigation (consolidated from useNavigation)
    menuOpen,
    toggleMenu,
    closeMenu,
    handleMobilePanel
  }
} 